import React, { useState } from 'react';
import { Modal, Button, Row, Col, Form, Card, ProgressBar, Badge } from 'react-bootstrap';

const BaseComparisonModal = ({ 
  show, 
  onHide, 
  title,
  type,
  getData,
  leftLabel,
  rightLabel,
  getAvailableOptions,
  students,
  onStudentChange, // Add this prop
  customHeader,
  disabled,
  loading,
  loadingMessage,
  error,
  emptyMessage,
  loadingOptions,
  selectedStudent: parentSelectedStudent // Get selected student from parent
}) => {
  const [selectedOption, setSelectedOption] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  
  // Use parent's selectedStudent state instead of local state
  const selectedStudent = parentSelectedStudent || '';

  const handleStudentChange = (studentId) => {
    console.log('🏫 BaseModal DEBUG: handleStudentChange called with:', studentId);
    setSelectedOption('');
    setComparisonData(null);
    
    // Call the parent's onStudentChange if provided
    if (onStudentChange) {
      console.log('🏫 BaseModal DEBUG: Calling parent onStudentChange with:', studentId);
      onStudentChange(studentId);
    } else {
      console.log('⚠️ BaseModal DEBUG: No onStudentChange prop provided');
    }
  };

  const handleOptionChange = async (option) => {
    console.log('🏫 BaseModal DEBUG: handleOptionChange called with:', option);
    setSelectedOption(option);
    if (selectedStudent && option) {
      console.log('🏫 BaseModal DEBUG: Getting data for student:', selectedStudent, 'option:', option);
      const data = await getData(selectedStudent, option);
      console.log('🏫 BaseModal DEBUG: Received data:', data);
      setComparisonData(data);
    }
  };

  const renderTaskResult = (task, side) => (
    <Card key={`${task.taskName}-${side}`} className="mb-2">
      <Card.Body className="py-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{task.taskName}</strong>
          <Badge variant={task.score >= 70 ? "success" : task.score >= 50 ? "warning" : "danger"}>
            {task.score}%
          </Badge>
        </div>
        <ProgressBar 
          now={task.score} 
          variant={task.score >= 70 ? "success" : task.score >= 50 ? "warning" : "danger"}
          className="mb-1"
        />
        <small className="text-muted">
          Time: {task.timeSpent}s
        </small>
      </Card.Body>
    </Card>
  );

  const availableOptions = selectedStudent ? getAvailableOptions(selectedStudent) : [];
  const selectedStudentName = students?.find(s => s.id.toString() === selectedStudent)?.name || '';

  console.log('🏫 BaseModal DEBUG: Render state:', {
    selectedStudent,
    availableOptions,
    selectedStudentName,
    studentsLength: students?.length || 0
  });

  // Show loading state
  if (loading) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {customHeader && customHeader}
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">{loadingMessage || 'Loading...'}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Show error state
  if (error) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {customHeader && customHeader}
          <div className="alert alert-danger">
            <h5>Error</h5>
            <p>{error}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  // Show empty state
  if (emptyMessage) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {customHeader && customHeader}
          <div className="alert alert-info">
            <h5>No Data Available</h5>
            <p>{emptyMessage}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <style jsx>{`
        .scrollable-results::-webkit-scrollbar {
          width: 8px;
        }
        
        .scrollable-results::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .scrollable-results::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        
        .scrollable-results::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        
        .scrollable-results {
          scrollbar-width: thin;
          scrollbar-color: #c1c1c1 #f1f1f1;
        }
        
        .scroll-indicator {
          position: sticky;
          bottom: 0;
          background: linear-gradient(transparent, rgba(248, 249, 250, 0.9));
          text-align: center;
          padding: 5px;
          font-size: 0.75rem;
          color: #6c757d;
        }
      `}</style>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {customHeader && customHeader}
        
        <Row className="mb-4">
          <Col md={4}>
            <Form.Group>
              <Form.Label><strong>Select Student:</strong></Form.Label>
              <Form.Control 
                as="select" 
                value={selectedStudent} 
                onChange={(e) => handleStudentChange(e.target.value)}
                disabled={disabled}
              >
                <option value="">Choose a student...</option>
                {students?.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.name} ({student.class})
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label><strong>Select {type === 'task' ? 'Task' : 'Assessment Point'}:</strong></Form.Label>
              <Form.Control 
                as="select" 
                value={selectedOption} 
                onChange={(e) => handleOptionChange(e.target.value)}
                disabled={!selectedStudent || disabled || loadingOptions}
              >
                <option value="">
                  {loadingOptions ? 'Loading options...' : 'Choose an option...'}
                </option>
                {availableOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Control>
              {loadingOptions && (
                <small className="text-muted">
                  <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                  Loading assessment options...
                </small>
              )}
            </Form.Group>
          </Col>

          <Col md={4}>
            {selectedStudentName && (
              <div className="mt-4 pt-2">
                <strong>Comparing:</strong> {selectedStudentName}
                {selectedOption && <><br/><strong>Focus:</strong> {selectedOption}</>}
              </div>
            )}
          </Col>
        </Row>

        {comparisonData?.error && (
          <div className="alert alert-warning">
            <strong>No Data Available:</strong> {comparisonData.error}
          </div>
        )}

        {comparisonData && !comparisonData.error && (
          <Row>
            <Col md={6}>
              <Card>
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">{leftLabel}</h5>
                  {comparisonData.left.date && <small>📅 {comparisonData.left.date}</small>}
                  {comparisonData.left.isRepeated && (
                    <div className="mt-1">
                      <small className="badge bg-light text-dark">📚 Archived Data</small>
                    </div>
                  )}
                </Card.Header>
                <Card.Body 
                  className="p-0 scrollable-results"
                  style={{ 
                    maxHeight: '500px', 
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    position: 'relative'
                  }}
                >
                  <div className="p-3">
                    {comparisonData.left.noData ? (
                      <div className="alert alert-info mb-0">
                        <strong>No archived data available</strong> for this assessment in the repeated year
                      </div>
                    ) : comparisonData.left.results && comparisonData.left.results.length > 0 ? (
                      <>
                        {comparisonData.left.results.map(task => renderTaskResult(task, 'left'))}
                        <div className="mt-2 pt-2 border-top">
                          <small className="text-muted">
                            <strong>Total tasks:</strong> {comparisonData.left.results.length}
                            {comparisonData.left.isRepeated && <span className="ms-2 text-primary">📚 From archived records</span>}
                          </small>
                        </div>
                        {comparisonData.left.results.length > 5 && (
                          <div className="scroll-indicator">
                            ↕️ Scroll to see all tasks
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-muted mb-0">No task results available</p>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">{rightLabel}</h5>
                  {comparisonData.right.date && <small>📅 {comparisonData.right.date}</small>}
                  {comparisonData.right.isCurrent && (
                    <div className="mt-1">
                      <small className="badge bg-light text-dark">🎯 Live Data</small>
                    </div>
                  )}
                  {comparisonData.right.interventionType && (
                    <><br/><small>Intervention: {comparisonData.right.interventionType}</small></>
                  )}
                </Card.Header>
                <Card.Body 
                  className="p-0 scrollable-results"
                  style={{ 
                    maxHeight: '500px', 
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    position: 'relative'
                  }}
                >
                  <div className="p-3">
                    {comparisonData.right.noData ? (
                      <div className="alert alert-info mb-0">
                        <strong>No current data available</strong> for this assessment in the current year
                      </div>
                    ) : comparisonData.right.results && comparisonData.right.results.length > 0 ? (
                      <>
                        {comparisonData.right.results.map(task => renderTaskResult(task, 'right'))}
                        <div className="mt-2 pt-2 border-top">
                          <small className="text-muted">
                            <strong>Total tasks:</strong> {comparisonData.right.results.length}
                            {comparisonData.right.isCurrent && <span className="ms-2 text-success">🎯 From current assessments</span>}
                          </small>
                        </div>
                        {comparisonData.right.results.length > 5 && (
                          <div className="scroll-indicator">
                            ↕️ Scroll to see all tasks
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-muted mb-0">No task results available</p>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
        {comparisonData && !comparisonData.error && (
          <Button variant="primary">Export Comparison</Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BaseComparisonModal;